import { ReactNode, useCallback, useRef, useState } from 'react';

import { ImmersiveModalWrapper } from 'src/components/dialogs/ImmersiveModalWrapper';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { ActionButtonModal } from 'src/legacy/components/Modals';
import { setFormPageChanged } from 'src/store/ui/actions';

interface BuilderModalProps<TCreated> {
  isOpen: boolean;
  /** Fired once the builder is left, with the created record when it finished. */
  onClose: (created?: TCreated) => void;
  /** Extra store cleanup that runs alongside the dirty-flag reset on every exit. */
  onReset?: () => void;
  /** Renders the builder; it reports back through the given close handler. */
  children: (onClose: (created?: TCreated) => void) => ReactNode;
}

/**
 * Fullscreen host for the create-flow builders (invoice, subscription,
 * payment link, form, contract). It owns the shared exit rules: a dirty form
 * gets an "Unsaved changes" confirmation, while a finished build or a clean
 * form closes straight away and clears the dirty flag.
 */
export const BuilderModal = <TCreated,>({
  isOpen,
  onClose,
  onReset,
  children,
}: BuilderModalProps<TCreated>) => {
  const dispatch = useAppDispatch();
  const isFormPageChanged = useAppSelector(
    (state) => state.ui.isFormPageChanged,
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Refs keep the close handlers stable while the dirty flag flips on every
  // keystroke, so the re-render does not cascade into the builder's Formik tree.
  const isFormPageChangedRef = useRef(isFormPageChanged);
  isFormPageChangedRef.current = isFormPageChanged;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;

  const leave = useCallback(
    (created?: TCreated) => {
      dispatch(setFormPageChanged(false));
      onResetRef.current?.();
      setShowConfirmDialog(false);
      onCloseRef.current(created);
    },
    [dispatch],
  );

  const handleClose = useCallback(() => {
    if (isFormPageChangedRef.current) {
      setShowConfirmDialog(true);
      return;
    }
    leave();
  }, [leave]);

  const handleCloseFromContent = useCallback(
    (created?: TCreated) => {
      if (!created && isFormPageChangedRef.current) {
        setShowConfirmDialog(true);
        return;
      }
      leave(created);
    },
    [leave],
  );

  const handleConfirmLeave = useCallback(() => leave(), [leave]);

  const handleCancelLeave = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <ImmersiveModalWrapper isOpen={isOpen} onClose={handleClose}>
        {children(handleCloseFromContent)}
      </ImmersiveModalWrapper>
      <ActionButtonModal
        open={showConfirmDialog}
        onClose={handleCancelLeave}
        title="Unsaved changes"
        mainComponent="If you leave this page, your changes will be lost."
        cancelActionText="Cancel"
        positiveActionText="Leave page"
        positiveAction
        onPositiveActionPerformed={handleConfirmLeave}
      />
    </>
  );
};
